import { ApplicationConnectionErrorCode } from 'models'

/* Every provider-specific word on the application setup page. The page shell holds none of it, so
 * a new outbound integration is a manifest plus one entry here, keyed by the manifest's
 * `config.oauth_provider`. Scopes are deliberately absent: they arrive with the manifest. */
export interface ApplicationProviderCopy {
  authMethodLabel: string
  authMethodReassurance: string
  serviceAccountTitle: string
  serviceAccountBody: string
  connectButtonLabel: string
  siteLabel: string
  permissionsLabel: string
  errorMessages?: Partial<Record<ApplicationConnectionErrorCode, string>>
}

const DEFAULT_ERROR_MESSAGES: Record<ApplicationConnectionErrorCode, string> = {
  invalid_state: 'The approval could not be matched to this setup. Please try again.',
  access_denied: 'The approval was declined, so Onyxia was not granted access.',
  token_exchange_failed: 'The provider did not complete the approval. Please try again.',
  secret_storage_failed: 'Onyxia could not store the approval securely. Please try again.',
  provider_unavailable: 'The provider could not be reached. Please try again in a few minutes.',
  ambiguous_account: 'The approving account reaches more than one site, so Onyxia cannot tell which one to use.',
  provider_not_configured: 'This integration is not available on your account yet. Please contact Onyxia support.'
}

const GENERIC_ERROR_MESSAGE = 'The connection could not be completed. Please try again.'

export const APPLICATION_PROVIDERS: Record<string, ApplicationProviderCopy> = {
  atlassian: {
    authMethodLabel: 'OAuth 2.0',
    authMethodReassurance:
      'You approve the connection in Atlassian. Onyxia never sees or stores your password or API token.',
    serviceAccountTitle: 'Use a dedicated service account',
    serviceAccountBody:
      'Sign in with an Atlassian account your team controls rather than your own. Tickets Onyxia creates are raised under that identity, and its Jira access stays limited to what this integration needs.',
    connectButtonLabel: 'Connect to Jira',
    siteLabel: 'Jira site',
    permissionsLabel: 'Permissions granted',
    errorMessages: {
      access_denied: 'The approval was declined in Atlassian, so Onyxia was not granted access to your Jira.',
      ambiguous_account:
        'The approving Atlassian account reaches more than one Jira site, so Onyxia cannot tell which one to use.',
      provider_unavailable: 'Atlassian could not be reached. Please try again in a few minutes.'
    }
  }
}

export const providerCopy = (provider?: string): ApplicationProviderCopy | undefined =>
  provider ? APPLICATION_PROVIDERS[provider] : undefined

/* Takes a plain string because the code reaches here from the connection block and from the
 * callback's query alike, and a code newer than this frontend must fall back rather than throw. */
export const connectionErrorMessage = (copy: ApplicationProviderCopy | undefined, code: string | null): string => {
  if (!code) return GENERIC_ERROR_MESSAGE

  const known = code as ApplicationConnectionErrorCode

  return copy?.errorMessages?.[known] ?? DEFAULT_ERROR_MESSAGES[known] ?? GENERIC_ERROR_MESSAGE
}
